import { Navigate, useParams } from 'react-router-dom';
import { LoadingSpinner, QueryErrorAlert, TextLink } from '@shared/components';
import { useAgreementByQuoteQuery } from '../hooks/useAgreementByQuoteQuery';

/** Resolver route: turns a quote id into its Agreement and redirects, so the lookup
 *  has its own URL and never runs inside a click handler. */
export function AgreementByQuote() {
    const { quoteId } = useParams<{ quoteId: string }>();
    const query = useAgreementByQuoteQuery(quoteId);

    if (!quoteId) {
        return (
            <QueryErrorAlert message="Invalid quote reference." />
        );
    }

    if (query.isLoading) {
        return <LoadingSpinner message="Finding agreement" className="p-4" />;
    }

    if (query.isError) {
        return (
            <QueryErrorAlert
                message="Failed to look up the agreement for this quote."
                onRetry={() => query.refetch()}
                isRetrying={query.isFetching}
            />
        );
    }

    if (!query.data) {
        return <NoAgreementForQuote />;
    }

    // `replace` so the resolver never appears in history — Back from the
    // detail page returns to Agreements, not into a redirect loop.
    return <Navigate to={`/agreements/${query.data.id}`} replace />;
}

/** Expected, not an error: quotes older than agreements-service have no Agreement.
 *  The copy must not reveal whether one exists outside the user's dealers. */
function NoAgreementForQuote() {
    return (
        <div className="space-y-3 rounded-md border border-mp-border bg-mp-surface p-6 text-sm">
            <p className="font-medium text-mp-foreground">
                No agreement is available for this quote.
            </p>
            <p className="text-mp-muted">
                It may not have been created yet, or it may belong to a dealership you do not have
                access to.
            </p>
            <TextLink to="/agreements" color="secondary" className="text-sm">
                &larr; Back to Agreements
            </TextLink>
        </div>
    );
}
